// The pure part of the one table (02-DESIGN/03-console/01-the-console.md
// § conventions: a table; a row's actions; remove): how rows sort on a
// column, how the *Filter* field and the *Status: any* menus narrow them, and
// the order of a row menu's entries. Sorting and filtering are the browser's,
// over the rows the page read. Nothing here knows React or the wire.

/** SortDir is a sorted column's direction: once ascending, again descending. */
export type SortDir = "asc" | "desc";

/** Sort names the column a table is sorted by and its direction. */
export interface Sort {
  key: string;
  dir: SortDir;
}

/** Sortable is what the pure part reads of a column. */
export interface Sortable<R> {
  key: string;
  /** The value the column sorts by; a column without one does not sort. */
  value?: (r: R) => string | number;
  /** The text the filter reads in this column; the value's text where omitted. */
  text?: (r: R) => string;
  /** Whether a click on the header sorts; true wherever the column has a value. */
  sortable?: boolean;
  /** A state or kind column's menu label — *Status*, *Kind* — narrowing by the value. */
  facet?: string;
}

/** isSortable says whether a click on the column's header sorts it. */
export function isSortable<R>(c: Sortable<R>): boolean {
  return c.value !== undefined && c.sortable !== false;
}

/** nextSort is the sort after a click on a header: a new column ascending, the sorted one turned around. */
export function nextSort(current: Sort, key: string): Sort {
  if (current.key !== key) {
    return { key, dir: "asc" };
  }
  return { key, dir: current.dir === "asc" ? "desc" : "asc" };
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** compareValues orders two cell values: numbers by size, text as a person reads it. */
export function compareValues(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return collator.compare(String(a), String(b));
}

/** sortRows returns the rows sorted on a column, stable, leaving the input untouched. */
export function sortRows<R>(rows: readonly R[], columns: readonly Sortable<R>[], sort: Sort): R[] {
  const col = columns.find((c) => c.key === sort.key);
  const value = col?.value;
  if (value === undefined) {
    return [...rows];
  }
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: value(r) }))
    .sort((x, y) => sign * compareValues(x.v, y.v) || x.i - y.i)
    .map((x) => x.r);
}

function textOf<R>(c: Sortable<R>, r: R): string {
  if (c.text !== undefined) {
    return c.text(r);
  }
  if (c.value !== undefined) {
    return String(c.value(r));
  }
  return "";
}

/** matches says whether a row holds every word of the filter in the text of its shown columns. */
export function matches<R>(row: R, columns: readonly Sortable<R>[], query: string): boolean {
  const words = query
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter((w) => w !== "");
  if (words.length === 0) {
    return true;
  }
  const hay = columns
    .map((c) => textOf(c, row))
    .join("\n")
    .toLocaleLowerCase();
  return words.every((w) => hay.includes(w));
}

/** facetOptions are the distinct values a state or kind column holds, in order. */
export function facetOptions<R>(rows: readonly R[], column: Sortable<R>): string[] {
  const value = column.value;
  if (value === undefined) {
    return [];
  }
  const seen = new Set<string>();
  for (const r of rows) {
    seen.add(String(value(r)));
  }
  return [...seen].sort((a, b) => collator.compare(a, b));
}

/** Facets maps a facet column's key to the value chosen in its menu; absent is *any*. */
export type Facets = Readonly<Record<string, string>>;

/** shownRows is what the table draws: the rows the filter and the menus keep, in the sort's order. */
export function shownRows<R>(
  rows: readonly R[],
  columns: readonly Sortable<R>[],
  { query, facets, sort }: { query: string; facets: Facets; sort: Sort },
): R[] {
  const kept = rows.filter((r) => {
    for (const c of columns) {
      const want = facets[c.key];
      if (want !== undefined && c.value !== undefined && String(c.value(r)) !== want) {
        return false;
      }
    }
    return matches(r, columns, query);
  });
  return sortRows(kept, columns, sort);
}

/** Ordered is a menu entry that may be a remove: drawn last, after a divider, in the danger colour. */
export interface Ordered {
  danger?: boolean;
}

/** orderMenu puts a row menu's entries in order: everything else as given, then the removes. */
export function orderMenu<E extends Ordered>(entries: readonly E[]): { rest: E[]; removes: E[] } {
  return {
    rest: entries.filter((e) => e.danger !== true),
    removes: entries.filter((e) => e.danger === true),
  };
}
